// Servicio del flujo Consentimiento → FODA → PDI → Agenda.
// Envuelve los endpoints /api/plan/* y devuelve directamente el payload (res.data.data).
import apiService from './api';

const unwrap = (r: any) => r?.data?.data ?? r?.data;

export type PlanRole = 'mentee' | 'mentor' | 'admin';

const planApi = {
  // Duplas del usuario
  getMy: async () => unwrap(await apiService.get('/plan/my')),
  // Todas las duplas (admin)
  getAll: async () => unwrap(await apiService.get('/plan/all')),
  // Hitos de las duplas (admin): estado FODA/PDI por dupla
  getMilestones: async () => unwrap(await apiService.get('/plan/milestones')),
  // Resumen del programa (admin)
  getAdminOverview: async () => unwrap(await apiService.get('/plan/admin-overview')),
  // Calificaciones de sesión (admin): vista confidencial mentor vs mentee
  getRatings: async () => unwrap(await apiService.get('/plan/ratings')),
  // Agendamiento (admin): grilla mensual M1..M12 por dupla
  getScheduling: async () => unwrap(await apiService.get('/plan/scheduling')),
  // Avance del PDI (admin): lista de duplas con progreso, y detalle por dupla
  getPdiProgress: async () => unwrap(await apiService.get('/plan/pdi-progress')),
  getPdiDetail: async (relId: number) => unwrap(await apiService.get(`/plan/relationship/${relId}/pdi-detail`)),

  // Estado global del plan de una dupla
  getPlan: async (relId: number) => unwrap(await apiService.get(`/plan/relationship/${relId}`)),

  // Consentimiento / Acuerdo
  acceptConsent: async (relId: number) =>
    unwrap(await apiService.post(`/plan/relationship/${relId}/consent/accept`)),
  signAgreement: async (relId: number, answers: Record<string, string>) =>
    unwrap(await apiService.post(`/plan/relationship/${relId}/consent/sign`, { answers })),

  // FODA
  getFoda: async (relId: number) => unwrap(await apiService.get(`/plan/relationship/${relId}/foda`)),
  createFodaItem: async (relId: number, data: { quadrant: string; content: string; evidence?: string; area?: string }) =>
    unwrap(await apiService.post(`/plan/relationship/${relId}/foda`, data)),
  updateFodaItem: async (itemId: number, data: any) =>
    unwrap(await apiService.put(`/plan/foda/${itemId}`, data)),
  deleteFodaItem: async (itemId: number) => unwrap(await apiService.delete(`/plan/foda/${itemId}`)),
  toggleSelectFoda: async (itemId: number) => unwrap(await apiService.put(`/plan/foda/${itemId}/select`)),
  finalizeFoda: async (relId: number) =>
    unwrap(await apiService.post(`/plan/relationship/${relId}/foda/finalize`)),
  saveFodaComment: async (relId: number, comment: string) =>
    unwrap(await apiService.post(`/plan/relationship/${relId}/foda/comment`, { comment })),
  updateFodaComment: async (commentId: number, comment: string) =>
    unwrap(await apiService.put(`/plan/foda/comment/${commentId}`, { comment })),
  deleteFodaComment: async (commentId: number) =>
    unwrap(await apiService.delete(`/plan/foda/comment/${commentId}`)),

  // PDI
  getPdi: async (relId: number) => unwrap(await apiService.get(`/plan/relationship/${relId}/pdi`)),
  seedPdi: async (relId: number, fodaItemIds?: number[]) =>
    unwrap(await apiService.post(`/plan/relationship/${relId}/pdi/seed`, fodaItemIds ? { foda_item_ids: fodaItemIds } : {})),
  savePdiNorte: async (relId: number, norte: string) =>
    unwrap(await apiService.put(`/plan/relationship/${relId}/pdi/norte`, { norte })),
  addPdiComment: async (competencyId: number, text: string) =>
    unwrap(await apiService.post(`/plan/pdi/competency/${competencyId}/comment`, { text })),
  updateCompetency: async (id: number, data: any) =>
    unwrap(await apiService.put(`/plan/pdi/competency/${id}`, data)),
  deleteCompetency: async (id: number) =>
    unwrap(await apiService.delete(`/plan/pdi/competency/${id}`)),
  addAction: async (competencyId: number, data: { text: string; category: string; review_date?: string }) =>
    unwrap(await apiService.post(`/plan/pdi/competency/${competencyId}/action`, data)),
  updateAction: async (id: number, data: any) => unwrap(await apiService.put(`/plan/pdi/action/${id}`, data)),
  completeAction: async (id: number) => unwrap(await apiService.put(`/plan/pdi/action/${id}/complete`)),
  deleteAction: async (id: number) => unwrap(await apiService.delete(`/plan/pdi/action/${id}`)),
  finalizePdi: async (relId: number) =>
    unwrap(await apiService.post(`/plan/relationship/${relId}/pdi/finalize`)),

  // Agenda
  getAvailability: async (relId: number) => unwrap(await apiService.get(`/plan/relationship/${relId}/availability`)),
  publishAvailability: async (relId: number, slots: Array<{ slot_datetime: string; duration_minutes?: number }>) =>
    unwrap(await apiService.post(`/plan/relationship/${relId}/availability`, { slots })),
  deleteSlot: async (slotId: number) => unwrap(await apiService.delete(`/plan/availability/${slotId}`)),
  bookSlot: async (slotId: number, data: { focus?: string; modality?: string; pdi_competency_ids?: number[] }) =>
    unwrap(await apiService.post(`/plan/availability/${slotId}/book`, data)),
  bookFree: async (relId: number, data: { focus?: string; scheduled_date: string; duration_minutes?: number; modality?: string; pdi_competency_ids?: number[] }) =>
    unwrap(await apiService.post(`/plan/relationship/${relId}/session/free`, data)),
  markOutcome: async (sessionId: number, data: { happened: boolean; feedback?: string }) =>
    unwrap(await apiService.put(`/plan/session/${sessionId}/outcome`, data)),
  updateSession: async (sessionId: number, data: { focus?: string; scheduled_date?: string; duration_minutes?: number; modality?: string; pdi_competency_ids?: number[] }) =>
    unwrap(await apiService.put(`/plan/session/${sessionId}`, data)),
  cancelSession: async (sessionId: number) =>
    unwrap(await apiService.put(`/plan/session/${sessionId}/cancel`)),
  registerSession: async (sessionId: number, data: { rating?: number; completed_objective?: boolean; feedback?: string; admin_note?: string; action_ids?: number[] }) =>
    unwrap(await apiService.put(`/plan/session/${sessionId}/register`, data)),
  deleteSession: async (sessionId: number) =>
    unwrap(await apiService.delete(`/plan/session/${sessionId}`)),

  // Sesiones
  getSessions: async (relId: number) => unwrap(await apiService.get(`/plan/relationship/${relId}/sessions`)),
  acceptSession: async (id: number) => unwrap(await apiService.put(`/plan/session/${id}/accept`)),
  rejectSession: async (id: number) => unwrap(await apiService.put(`/plan/session/${id}/reject`)),
};

export default planApi;
